import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps,
  AutocompleteValue,
  Backdrop,
  Box,
  Button,
  ButtonProps,
  Divider,
  Autocomplete as MuiAutocomplete,
  Paper,
  PaperProps,
  Typography
} from "@mui/material";
import { useDidUpdate } from "hooks";

// I---------------------------------------------- How to use ----------------------------------------------I
//  There 2 possible states, controlled and uncontrolled:
//
//    1. When use uncontrolled, component will use local state to keep and manage data.
//      Component should get "value" and "setValue" props. In this state, component can get "defaultValue"
//      and "onChange", where last one is just a listener of component`s local state.
//      Sum up: don't pass "value" and "setValue";
//    Example:
//      <SelectAutocomplete<ObjectInterface, true>
//        options={options}
//        multiple -> optional
//        selectAll -> optional
//        onChange={(newValue) => showNewValue(option)} -> optional
//        defaultValue={[]} -> optional
//        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ObjectInterface) => (
//          <li {...props} key={option.id}>
//            {option.presentation_name}
//          </li>
//        )}
//        getOptionLabel={(option) => option.presentation_name}
//        renderInput={(params) => <TextField {...params} label="Entity" />}
//        compareParam={(p) => p.presentation_name} -> Required if options are not primitives (select all option)
//      />
//
//    2. When use controlled state, the "value" and "setValue" params should be passed from outside via props.
//      If component get "value" and "setValue" it will use outside state as own local.
//    Example:
//      <SelectAutocomplete<ObjectInterface, true>
//        options={options}
//        value={values.entity} -> required for controlled state
//        setValue={(newValue) => setNewValue(newValue)} -> required for controlled state
//        multiple -> optional
//        selectAll => optional
//        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ObjectInterface) => (
//          <li {...props} key={option.id}>
//            {option.presentation_name}
//          </li>
//        )}
//        getOptionLabel={(option) => option.presentation_name}
//        renderInput={(params) => <TextField {...params} label="Entity" />}
//        compareParam={(p) => p.presentation_name} -> Required if options are not primitives (select all option)
//      />
// I--------------------------------------------------------------------------------------------------------I

type CustomProps<T, M extends boolean | undefined = undefined, A = boolean | undefined> = {
  selectAll?: A;
  compareParam?: (a: T) => string; // if options are objects, copmareParam required for search and select all
  setValue?: (
    newValue: M extends true ? T[] : T | null,
    reason: AutocompleteChangeReason | "selectAll" | "unselectAll",
    details?: AutocompleteChangeDetails<T>
  ) => void; // Controlled state, value comes outside.
  onChange?: (newValue: M extends true ? T[] : T | null) => void; // Uncontrolled state, no value in props uses local state
  createNew?: {
    title: string;
    buttonProps?: ButtonProps;
  };
  showBackdrop?: boolean; // Optional backdrop
};

type Props<
  T,
  M extends boolean | undefined = undefined,
  D extends boolean | undefined = undefined,
  F extends boolean | undefined = undefined,
  A = boolean | undefined
> = (T extends object
  ? Omit<AutocompleteProps<T, M, D, F>, "getOptionLabel" | "renderOption" | "onChange" | "disableClearable"> &
      Required<Pick<AutocompleteProps<T, M, D, F>, "getOptionLabel" | "renderOption">>
  : Omit<AutocompleteProps<T, M, D, F>, "onChange" | "disableClearable">) &
  (T extends object
    ? Omit<CustomProps<T, M, A>, "compareParam"> & Required<Pick<CustomProps<T, M, A>, "compareParam">>
    : CustomProps<T, M, A>) & {
    disableClearable?: boolean;
  };

export function SelectAutocomplete<
  T,
  M extends boolean | undefined = undefined,
  D extends boolean | undefined = undefined,
  F extends boolean | undefined = undefined,
  A = boolean | undefined
>({
  selectAll,
  compareParam,
  value,
  onChange,
  defaultValue,
  setValue,
  createNew,
  showBackdrop,
  ...rest
}: Props<T, M, D, F, A>): JSX.Element {
  const { options, multiple, open } = rest;

  const defaultState: AutocompleteValue<T, M, D, F> = defaultValue || (multiple ? [] : (null as any));

  const [search, setSearch] = useState<string>("");
  const [isOpen, setIsOpen] = React.useState(open || false);
  const [state, setState] = useState<AutocompleteValue<T, M, D, F>>(defaultState);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const scrollPositionRef = useRef<number>(0);

  useDidUpdate(() => {
    onChange?.(state as M extends true ? T[] : T | null);
  }, [state]);

  const filterOptions = useCallback(({ options, search }: { options: readonly T[]; search: string }): T[] => {
    return options.filter((option) => {
      const searchValue =
        typeof option === "object" && compareParam
          ? compareParam(option).trim().toLowerCase()
          : (option as unknown as string).trim().toLowerCase();
      return searchValue.includes(search.trim().toLowerCase());
    });
  }, []);

  const elementsInList = useMemo(() => {
    if (!isOpen) return 0;
    if (!search.length) return options.length;
    return filterOptions({ options, search }).length;
  }, [filterOptions, options, search, isOpen]);

  const isAllFilteredSelected = useMemo(() => {
    if (!multiple || !isOpen) return false;
    const filteredOptions = search ? filterOptions({ options, search }) : options;
    const currentValue = value !== undefined && setValue ? (value as T[]) : (state as T[]);
    return filteredOptions.every((option) =>
      currentValue.some((selected) =>
        typeof option === "object" && compareParam
          ? compareParam(option) === compareParam(selected as any)
          : option === selected
      )
    );
  }, [multiple, isOpen, search, filterOptions, options, value, setValue, state, compareParam]);

  // Preserve scroll position after value changes
  useEffect(() => {
    if (listboxRef.current && isOpen) {
      // Restore scroll position using requestAnimationFrame for better timing
      const frameId = requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = scrollPositionRef.current;
        }
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [value !== undefined ? value : state, isOpen]);

  const handleSelectAll = useCallback(() => {
    if (!multiple) return;

    const filteredOptions = search ? filterOptions({ options, search }) : options;

    const currentValue = value !== undefined && setValue ? (value as T[]) : (state as T[]);

    const isAllSelected = filteredOptions.every((option) =>
      currentValue.some((selected) =>
        typeof option === "object" && compareParam
          ? compareParam(option) === compareParam(selected as any)
          : option === selected
      )
    );

    let resultValue: T[];

    if (isAllSelected) {
      // Remove filtered options from current value
      resultValue = currentValue.filter(
        (selected) =>
          !filteredOptions.some((option) =>
            typeof option === "object" && compareParam
              ? compareParam(option) === compareParam(selected as any)
              : option === selected
          )
      );
    } else {
      // Add filtered options to current value, avoiding duplicates
      resultValue = [...currentValue];
      filteredOptions.forEach((option) => {
        const exists = resultValue.some((selected) =>
          typeof option === "object" && compareParam
            ? compareParam(option) === compareParam(selected as any)
            : option === selected
        );
        if (!exists) {
          resultValue.push(option);
        }
      });
    }

    // Handle Controlled State
    if (value !== undefined && setValue) {
      setValue(resultValue as M extends true ? T[] : T | null, isAllSelected ? "unselectAll" : "selectAll");
    } else {
      // Handle uncontrolled state
      setState(resultValue as AutocompleteValue<T, M, D, F>);
    }
    setIsOpen(false);
  }, [options, multiple, state, search, compareParam, value, setValue, filterOptions]);

  const onSelectAll: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault();
      handleSelectAll();
    },
    [handleSelectAll]
  );

  return (
    <>
      {/* add backdrop, in cases where you want to block scrolling or other actions on the page while the list is open */}
      {showBackdrop && (
        <Backdrop
          open={isOpen}
          sx={{
            zIndex: 1200,
            backgroundColor: "transparent",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
      <MuiAutocomplete<T, M, D, F>
        {...rest}
        disableClearable={rest.disableClearable as D}
        disableScrollLock
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        slotProps={{
          ...rest.slotProps,
          listbox: {
            ...(typeof rest.slotProps?.listbox === "object" && !Array.isArray(rest.slotProps.listbox)
              ? rest.slotProps.listbox
              : {}),
            ref: (node: HTMLUListElement | null) => {
              listboxRef.current = node;
              // Handle forwarded ref if slotProps.listbox has one (and it's an object)
              const listboxSlotProps =
                typeof rest.slotProps?.listbox === "object" && !Array.isArray(rest.slotProps.listbox)
                  ? rest.slotProps.listbox
                  : null;
              if (listboxSlotProps?.ref) {
                if (typeof listboxSlotProps.ref === "function") {
                  (listboxSlotProps.ref as (node: HTMLUListElement | null) => void)(node);
                } else {
                  (listboxSlotProps.ref as React.MutableRefObject<HTMLUListElement | null>).current = node;
                }
              }
            },
            onScroll: (e: React.UIEvent<HTMLUListElement>) => {
              scrollPositionRef.current = e.currentTarget.scrollTop;
              const listboxSlotProps =
                typeof rest.slotProps?.listbox === "object" && !Array.isArray(rest.slotProps.listbox)
                  ? rest.slotProps.listbox
                  : null;
              listboxSlotProps?.onScroll?.(e);
            }
          },
          ...(!rest.slotProps?.paper && {
            paper: {
              ...rest.slotProps?.paper,
              component: (paperProps: PaperProps<React.ElementType, { component?: React.ElementType }>) => {
                const { children, ...restPaperProps } = paperProps;
                return (
                  <Paper {...restPaperProps}>
                    {selectAll &&
                      elementsInList > 0 &&
                      (typeof options[0] === "object" || typeof options[0] === "string") && (
                        <Box
                          onMouseDown={(e) => e.preventDefault()} // prevent blur
                          pl={1.5}
                          py={1.0}
                          onClick={onSelectAll}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)"
                            }
                          }}
                        >
                          <Typography>{isAllFilteredSelected ? "Unselect all" : "Select all"}</Typography>
                        </Box>
                      )}
                    {selectAll && elementsInList > 0 && <Divider sx={{ borderColor: "#dadce0" }} />}
                    {children}
                    {createNew && (
                      <Button
                        {...createNew.buttonProps}
                        onClick={(e) => {
                          e.stopPropagation();
                          createNew.buttonProps?.onClick?.(e);
                        }}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          height: "50px",
                          backgroundColor: "#fff",
                          boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                          borderRadius: "0 0 10px 10px"
                        }}
                        fullWidth
                      >
                        {createNew.title}
                      </Button>
                    )}
                  </Paper>
                );
              }
            }
          })
        }}
        onChange={(_, option, reason, details) => {
          // Save scroll position before state change
          if (listboxRef.current) {
            scrollPositionRef.current = listboxRef.current.scrollTop;
          }
          if (setValue && value !== undefined) {
            setValue(option as M extends true ? T[] : T | null, reason, details);
            // Restore scroll position after state update
            requestAnimationFrame(() => {
              if (listboxRef.current) {
                listboxRef.current.scrollTop = scrollPositionRef.current;
              }
            });
            return;
          }
          setState(option);
          // Restore scroll position after state update
          requestAnimationFrame(() => {
            if (listboxRef.current) {
              listboxRef.current.scrollTop = scrollPositionRef.current;
            }
          });
        }}
        value={value !== undefined && setValue ? value : state}
        onInputChange={(_, value) => setSearch(value)}
      />
    </>
  );
}
